/**
 * RoomInviteByToken — PPL-28 (web: public invitation landing).
 *
 * Public `/i/:token` landing. NO auth required — `resolveRoomInviteByToken`
 * is itself unauthenticated. Replaces the PPL-17 stub.
 *
 * Routing invariant: this route is a BARE route in App.jsx (outside
 * ProtectedRoute and APP_SHELL_PATHS), wrapped in <RoomErrorBoundary>.
 *
 * Behaviour by auth state
 * ────────────────────────
 * Logged-OUT:
 *   Accept       → /register?continueUrl=/i/:token?action=accept  (action folded
 *                  INTO continueUrl so it survives register→verify→login; PPL-159)
 *   Decline      → declineRoomInviteByToken(token) → home — no signup needed;
 *                  the callable is unauthenticated (token = capability), ends
 *                  the mediation and quietly notifies the inviter
 *   Tell me more → /i/:token/tell-me-more (after inline preview)
 *
 * Logged-IN:
 *   Accept       → acceptRoomInviteByToken(token) → /rooms/:id/prep
 *   Decline      → declineRoomInviteByToken(token) → /rooms
 *   Tell me more → inline preview → /i/:token/tell-me-more
 *
 * Post-signup bind: Register.jsx navigates to /i/:token?action=accept after
 * the user verifies their email. This component detects user + ?action=accept
 * and auto-calls acceptRoomInviteByToken to complete the token bind.
 *
 * Visual shell:
 *   - Bare page (no left rail) — mirrors /unsubscribe.
 *   - Conversation-feel: scripted f8 intro bubbles + 3 equal-weight CTAs.
 *   - Locked composer (disabled textarea) signals "this is f8".
 *   - Reuses F8Chat.css + RoomInvitationReceived.css.
 *
 * Privacy invariant: never renders anything beyond the RoomInvitePublicView
 * shape — inviter handle, firstName, profileImageUrl, reason, lifecycle flags.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUp, Mic } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import {
  acceptRoomInviteByToken,
  declineRoomInviteByToken,
  resolveRoomInviteByToken,
} from '../services/roomService';
import type { RoomInvitePublicView } from '../types/room';
import { capture, hashInviteToken } from '../services/posthog';
import '../styles/F8Chat.css';
import '../styles/RoomInvitationReceived.css';

// ─── Scripted projection ──────────────────────────────────────────────────────

interface ScriptMsg {
  id: string;
  sender: 'bot' | 'user';
  text: string;
}

function buildScript(opts: {
  inviterName: string | null;
  reason: string | null;
}): ScriptMsg[] {
  const who = opts.inviterName || 'someone';
  return [
    {
      id: 'f8-1',
      sender: 'bot',
      text: `hey. ${who} wants to open a room with you.`,
    },
    {
      id: 'f8-2',
      sender: 'bot',
      text: opts.reason
        ? `they wrote: "${opts.reason.trim()}"`
        : 'they did not add a reason.',
    },
    {
      id: 'f8-3',
      sender: 'bot',
      text: "you have three options. accept and i'll walk you both through prep with f8 first. ask for more before deciding. or decline — that ends here, on your terms.",
    },
  ];
}

const TELL_ME_MORE_USER: ScriptMsg = {
  id: 'me-tellmemore',
  sender: 'user',
  text: 'tell me more before i decide',
};

const TELL_ME_MORE_F8: ScriptMsg = {
  id: 'f8-tellmemore',
  sender: 'bot',
  text: 'okay. if you accept, you and they each do prep with me first — i ask questions, you say what matters, no one sees the other side until you both opt in. then we meet in a shared room with sharing rules you both set. you can pause or end anytime. nothing surfaces without your permission.',
};

type ResponseId = 'accept' | 'tell_me_more' | 'decline';

// ─── Terminal state helper ────────────────────────────────────────────────────

function TerminalState({
  heading,
  body,
  linkLabel = 'go to pplkpr →',
  linkTo = '/',
}: {
  heading: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'var(--bg-primary, #0e0712)',
      }}
    >
      <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <span className="f8-orb" style={{ display: 'inline-block', marginBottom: 20 }} />
        <h1
          style={{
            margin: '0 0 12px',
            fontSize: 24,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '-0.02em',
          }}
        >
          {heading}
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--text-muted, rgba(220,200,240,0.7))',
            lineHeight: 1.55,
          }}
        >
          {body}
        </p>
        <div style={{ marginTop: 32 }}>
          <Link
            to={linkTo}
            style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}
          >
            {linkLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const RoomInviteByToken: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const actionParam = searchParams.get('action') as ResponseId | null;

  // Token resolution
  const [view, setView] = useState<RoomInvitePublicView | null>(null);
  const [resolveLoading, setResolveLoading] = useState(true);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Response state
  const [responding, setResponding] = useState<ResponseId | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  // Persist tellMoreShown across the /i/:token ⇄ /i/:token/tell-me-more round
  // trip so the inline preview the invitee saw before reading the explainer
  // isn't wiped on remount. Keyed by token so different invites don't bleed.
  const tellMoreKey = token ? `pplkpr:tellMoreShown:${token}` : '';
  const [tellMoreShown, setTellMoreShown] = useState<boolean>(() => {
    try {
      return !!tellMoreKey && sessionStorage.getItem(tellMoreKey) === '1';
    } catch {
      return false;
    }
  });
  const [showTyping, setShowTyping] = useState(false);
  // Track if auto-accept has fired (guard against double-fire)
  const [autoAcceptFired, setAutoAcceptFired] = useState(false);

  // Scripted messages — rebuild when view arrives
  const initialMessages = useMemo<ScriptMsg[]>(() => {
    if (!view) return [];
    return buildScript({
      inviterName: view.inviter.firstName ?? view.inviter.tetherHandle ?? null,
      reason: view.reason ?? null,
    });
  }, [view]);

  const [messages, setMessages] = useState<ScriptMsg[]>([]);

  // When initial messages are ready, seed the chat. If the invitee previously
  // tapped "tell me more" (persisted to sessionStorage above), replay the
  // inline preview so they come back to the same state.
  useEffect(() => {
    if (initialMessages.length === 0) return;
    if (tellMoreShown) {
      setMessages([...initialMessages, TELL_ME_MORE_USER, TELL_ME_MORE_F8]);
    } else {
      setMessages(initialMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    const el = document.querySelector('.medinv-messages');
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, showTyping]);

  // Resolve token on mount
  useEffect(() => {
    if (!token) {
      setResolveError('missing invite token.');
      setResolveLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await resolveRoomInviteByToken(token);
        if (!cancelled) {
          setView(data);
          // Referral-funnel stitching anchor: MUST fire for anonymous visitors
          // too (this anon distinct_id is what alias-at-signup later joins on).
          // The raw token is never sent — only its truncated hash.
          const inviteTokenHash = await hashInviteToken(token);
          capture('room_invite_opened', {
            invite_token_hash: inviteTokenHash,
            mediation_id: data.mediationId ?? null,
            expired: !!data.expired,
            already_accepted: !!data.alreadyAccepted,
            viewer_logged_in: !!user,
            viewer_role: data.viewerRole ?? 'anonymous',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setResolveError(
            err instanceof Error ? err.message : 'could not load this invite.',
          );
        }
      } finally {
        if (!cancelled) setResolveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-accept: user just signed up + navigated back here with ?action=accept
  useEffect(() => {
    if (!user || !token || actionParam !== 'accept' || autoAcceptFired) return;
    setAutoAcceptFired(true);
    // Strip the query param so a back-nav doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);
    void doAccept(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, actionParam, autoAcceptFired]);

  const doAccept = async (tok: string) => {
    setResponding('accept');
    setResponseError(null);
    try {
      const result = await acceptRoomInviteByToken(tok);
      navigate(`/rooms/${result.mediationId}/prep`, { replace: true });
    } catch (err) {
      setResponseError(
        err instanceof Error ? err.message : 'could not accept the invite.',
      );
    } finally {
      setResponding(null);
    }
  };

  const handleResponse = async (responseId: ResponseId) => {
    if (responding) return;
    setResponseError(null);

    // Landing response — fire-and-forget so hashing never delays the tap.
    void (async () => {
      const inviteTokenHash = token ? await hashInviteToken(token) : null;
      capture('room_invite_landing_response', {
        response: responseId,
        logged_in: !!user,
        invite_token_hash: inviteTokenHash,
        mediation_id: view?.mediationId ?? null,
      });
    })();

    if (responseId === 'tell_me_more') {
      if (tellMoreShown) {
        // Second tap: go to full explainer
        navigate(`/i/${token}/tell-me-more`);
        return;
      }
      // First tap: inline preview. Persist the flag so a round-trip through
      // the /i/:token/tell-me-more explainer doesn't reset the preview.
      setTellMoreShown(true);
      try {
        if (tellMoreKey) sessionStorage.setItem(tellMoreKey, '1');
      } catch { /* ignore quota / privacy-mode errors */ }
      setMessages((prev) => [...prev, TELL_ME_MORE_USER]);
      setShowTyping(true);
      window.setTimeout(() => {
        setShowTyping(false);
        setMessages((prev) => [...prev, TELL_ME_MORE_F8]);
      }, 700);
      return;
    }

    if (!token) {
      setResponseError('missing invite token.');
      return;
    }

    if (responseId === 'accept') {
      if (user) {
        await doAccept(token);
      } else {
        navigate(
          // Fold `action` INTO continueUrl (not a sibling param) so it survives the
          // register → verify → login round-trip and the auto-accept re-fires (PPL-159).
          `/register?continueUrl=${encodeURIComponent(`/i/${token}?action=accept`)}`,
        );
      }
      return;
    }

    if (responseId === 'decline') {
      // Token-based decline for BOTH logged-in and logged-out visitors: the
      // callable is unauthenticated (declining never requires signup), and a
      // logged-in-but-unbound invitee can't use the mediationId path anyway —
      // respondToRoomInvite's ownership guard rejects a placeholder invite
      // whose inviteeUid hasn't been bound yet. Records the decline (state →
      // 'ended') and gives the inviter a quiet in-app notice.
      setResponding('decline');
      try {
        await declineRoomInviteByToken(token);
      } catch (err) {
        setResponseError(
          err instanceof Error ? err.message : 'could not record your decline — please try again',
        );
        setResponding(null);
        return;
      }
      setResponding(null);
      navigate(user ? '/rooms' : '/');
    }
  };

  // ─── Loading ────────────────────────────────────────────────────────────────

  if (resolveLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-primary, #0e0712)',
        }}
      >
        <div className="f8-loading-bubble">
          <span className="f8-orb f8-orb-sm" />
          <div className="f8-loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  // ─── Error ──────────────────────────────────────────────────────────────────

  if (resolveError) {
    return <TerminalState heading="invite not available" body={resolveError} />;
  }

  // ─── Expired ────────────────────────────────────────────────────────────────

  if (view?.expired) {
    return (
      <TerminalState
        heading="this invite has expired"
        body="ask the person who invited you to send a new one."
      />
    );
  }

  // ─── Already accepted ───────────────────────────────────────────────────────

  if (view?.alreadyAccepted) {
    return (
      <TerminalState
        heading="already accepted"
        body={
          user
            ? 'this invite was already opened. go to your mediations to continue.'
            : 'this invite has already been opened. log in to see your mediation.'
        }
        linkLabel={user ? 'go to rooms →' : 'log in →'}
        linkTo={user ? '/rooms' : '/login'}
      />
    );
  }

  // ─── Wrong account ──────────────────────────────────────────────────────────
  // PPL-60 — gate the Accept/Decline UI BEFORE the user taps and hits the
  // redeem rejection. The server computes `viewerRole` per signed-in viewer.

  if (view?.viewerRole === 'inviter') {
    return (
      <TerminalState
        heading="this is your invitation"
        body="you sent this. waiting for the other person to open it. you can check status from your rooms."
        linkLabel="go to your rooms →"
        linkTo="/rooms"
      />
    );
  }

  if (view?.viewerRole === 'stranger') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--bg-primary, #0e0712)',
        }}
      >
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <span className="f8-orb" style={{ display: 'inline-block', marginBottom: 20 }} />
          <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
            this invitation isn&apos;t for you
          </h1>
          <p style={{ margin: 0, color: 'var(--text-muted, rgba(220,200,240,0.7))', lineHeight: 1.55 }}>
            you&apos;re signed in as someone other than the person who was invited. sign out and log in with the account the invite was sent to — you&apos;ll come straight back here.
          </p>
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={async () => {
                try {
                  await logout();
                } catch (err) {
                  console.warn('sign-out failed', err);
                }
                // Hand off to /login with a redirect back here (+action=accept) so
                // the right account lands straight in the auto-accept path — a bare
                // reload just re-showed the invite landing to the signed-out user.
                window.location.href = `/login?redirect=${encodeURIComponent(`/i/${token}?action=accept`)}`;
              }}
              style={{
                appearance: 'none', cursor: 'pointer', border: 0,
                padding: '12px 22px', borderRadius: 999,
                background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
              }}
            >
              sign out + log in
            </button>
            <Link
              to="/rooms"
              style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
            >
              go to your rooms instead →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main landing ────────────────────────────────────────────────────────────

  const inviterName =
    view?.inviter.firstName ?? view?.inviter.tetherHandle ?? null;
  const isAccepting = responding === 'accept';

  return (
    <div
      className="f8-chat-shell medinv-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--bg-primary, #0e0712)',
      }}
    >
      <div className="f8-conversation">
        {/* Header */}
        <div className="f8-conv-header">
          <div className="f8-conv-header-left">
            <span className="f8-orb f8-orb-sm" />
            <span className="f8-conv-title">f8 · room invite</span>
          </div>
          {view?.inviter.profileImageUrl && (
            <img
              src={view.inviter.profileImageUrl}
              alt={inviterName ?? ''}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            />
          )}
        </div>

        {/* Scripted conversation bubbles */}
        <div className="f8-messages medinv-messages">
          <div className="f8-messages-inner">
            {messages.map((m) => (
              <div key={m.id} className="f8-msg-group">
                {m.sender === 'user' ? (
                  <div className="f8-user-bubble">
                    <span className="f8-msg-text">{m.text}</span>
                  </div>
                ) : (
                  <div className="f8-agent-wrap">
                    <div className="f8-agent-bubble-border">
                      <div className="f8-agent-bubble">
                        <span className="f8-msg-text">{m.text}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {showTyping && (
              <div className="f8-agent-wrap">
                <div className="f8-agent-bubble-border">
                  <div className="f8-agent-bubble f8-loading-bubble">
                    <span className="f8-loading-label">thinking</span>
                    <div className="f8-loading-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* After inline tell-me-more preview, link to full explainer */}
            {tellMoreShown && !showTyping && (
              <div className="f8-agent-wrap" style={{ marginTop: 4 }}>
                <div className="f8-agent-bubble-border">
                  <div className="f8-agent-bubble">
                    <Link
                      to={`/i/${token}/tell-me-more`}
                      state={{ inviterHandle: inviterName }}
                      style={{
                        color: '#a78bfa',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                        fontSize: 13.5,
                        fontWeight: 500,
                      }}
                    >
                      read the full explainer →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {!!responseError && (
              <div className="medinv-error" role="alert">
                {responseError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Three equal-weight response buttons — no primary/secondary hierarchy */}
      <div className="medinv-responses">
        <button
          type="button"
          className="medinv-response medinv-response-accept"
          onClick={() => handleResponse('accept')}
          disabled={!!responding}
        >
          {isAccepting ? 'accepting…' : user ? 'accept' : 'accept — sign up first'}
        </button>

        <button
          type="button"
          className="medinv-response medinv-response-tell_me_more"
          onClick={() => handleResponse('tell_me_more')}
          disabled={!!responding}
        >
          tell me more before i decide
        </button>

        <button
          type="button"
          className="medinv-response medinv-response-decline"
          onClick={() => handleResponse('decline')}
          disabled={!!responding}
        >
          {responding === 'decline' ? 'declining…' : 'decline'}
        </button>
      </div>

      {/* Locked composer — signals "this is f8" but inputs are inert */}
      <div className="f8-composer-area medinv-composer-area">
        <div className="f8-composer-border">
          <div className="f8-composer-inner medinv-composer-inner">
            <div className="f8-composer-top">
              <textarea
                className="f8-textarea"
                placeholder={user ? 'use the buttons above to respond' : 'sign up to reply'}
                disabled
                readOnly
                rows={1}
                aria-label="composer disabled — use the buttons above"
              />
            </div>
            <div className="f8-composer-bottom">
              <div className="f8-composer-right">
                <span className="f8-hint medinv-composer-hint">
                  {user ? 'free chat opens once you accept' : 'sign up to join the conversation'}
                </span>
                <button
                  type="button"
                  className="f8-mic-btn"
                  disabled
                  aria-disabled="true"
                  title="disabled"
                >
                  <Mic size={16} />
                </button>
                <button
                  type="button"
                  className="f8-send-btn"
                  disabled
                  aria-disabled="true"
                  title="disabled"
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomInviteByToken;
